import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Text, Input, HStack,
} from '@chakra-ui/react';

/**
 * Reusable confirmation / prompt modal.
 *
 * Props:
 *   isOpen, onClose — standard disclosure
 *   title           — header text (default "Confirm")
 *   message         — body text
 *   confirmLabel    — confirm button label (default "Confirm")
 *   confirmColor    — confirm button colorScheme (default "red")
 *   onConfirm       — called with (inputValue) on confirm
 *   showInput       — if true, shows a text input (prompt mode)
 *   inputLabel      — label above input
 *   inputDefault    — default value for input
 *   inputPlaceholder — placeholder
 */
export default function ConfirmModal({
  isOpen, onClose, title = 'Confirm', message, confirmLabel = 'Confirm',
  confirmColor = 'red', onConfirm, showInput = false, inputLabel,
  inputDefault = '', inputPlaceholder = '',
}) {
  const [inputValue, setInputValue] = useState(inputDefault);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) setInputValue(inputDefault);
  }, [isOpen, inputDefault]);

  useEffect(() => {
    if (isOpen && showInput && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, showInput]);

  const handleConfirm = () => {
    if (onConfirm) onConfirm(showInput ? inputValue : undefined);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {message && <Text mb={showInput ? 3 : 0}>{message}</Text>}
          {showInput && (
            <>
              {inputLabel && <Text fontSize="12px" fontWeight="600" color="gray.600" mb={1}>{inputLabel}</Text>}
              <Input ref={inputRef} size="sm" value={inputValue} placeholder={inputPlaceholder}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm(); }} />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2}>
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button size="sm" colorScheme={confirmColor} onClick={handleConfirm}>{confirmLabel}</Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
